import { useDeferredValue, useId, useMemo, type FC, type ReactNode, type Ref } from "react";
import Stack from "@mui/material/Stack";
import {
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
  useGridSelector,
  gridFilterActiveItemsSelector,
} from "@mui/x-data-grid-premium";
import { useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { Button, ToggleButton } from "@mui/material";
import { Checkbox, HACK_alignIconWithLabelBox } from "@privacy/ui";
import { Columns3Icon, FunnelIcon } from "../shared/icons";
import { Grid } from "../shared/Grid";
import { PAGE_PADDING_X } from "../shared/Page";
import { SavedFilters } from "./SavedFilters";
import { useGridContext, useInitialisedGridApiRef, WithInitialisedGridApiRef } from "./DynamicGrid";
import { SidePanelTabs } from "./SidePanels";
import { useSearchParams } from "react-router";

type GridToolbarProps = {
  actionButtons?: ReactNode;
  filterButtons?: ReactNode;
  pageTitle: ReactNode;
};

export const Toolbar: FC<GridToolbarProps> = ({ actionButtons, filterButtons, pageTitle }) => {
  const { savedFiltersProps, texts, selectedRowId, sidePanelProps, toolbarTabsProps, showArchivedFilter } =
    useGridContext();

  const toolbarTabUtils = useGetActiveToolbarTabValue();

  return (
    <Stack sx={{ overflowX: "clip", overflowClipMargin: PAGE_PADDING_X }}>
      <Stack
        direction="row"
        sx={{ pb: 1, alignItems: "start", gap: 3, justifyContent: "space-between", px: PAGE_PADDING_X }}
      >
        {pageTitle}

        {!selectedRowId && (
          <WithInitialisedGridApiRef>
            <Stack direction="row" sx={{ gap: 2, alignItems: "baseline" }}>
              {actionButtons}
            </Stack>
          </WithInitialisedGridApiRef>
        )}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" sx={{ width: "100%", px: PAGE_PADDING_X, gap: 5, pb: 2 }}>
        <WithInitialisedGridApiRef>
          {gridApiRef => (
            <>
              {(() => {
                if (!toolbarTabsProps) return null;

                const activeTab = toolbarTabUtils.getActiveTabValue(toolbarTabsProps.queryKey, toolbarTabsProps.tabs);
                return (
                  <Stack direction="row" sx={{ gap: 0.5 }}>
                    {toolbarTabsProps.tabs.map(tab => (
                      <ToolbarTab
                        key={tab.value}
                        selected={activeTab === tab.value}
                        value={tab.value}
                        onChange={() => toolbarTabUtils.setActiveTabValue(toolbarTabsProps.queryKey, tab.value)}
                        labelKey={tab.labelKey}
                      />
                    ))}
                  </Stack>
                );
              })()}
              <Stack direction={"row"} sx={{ gap: 5 }}>
                {savedFiltersProps && (
                  <Box sx={{ flexShrink: 0 }}>
                    <SavedFilters {...savedFiltersProps} />
                  </Box>
                )}
                <Box sx={{ flexShrink: 0, minWidth: ["auto", "150px"] }}>
                  <Grid.SearchField gridApiRef={gridApiRef} placeholder={texts?.searchFieldPlaceholder} />
                </Box>
                {(filterButtons || showArchivedFilter) && (
                  <Stack direction="row" sx={{ gap: 1 }}>
                    {filterButtons}
                    {showArchivedFilter && <ShowArchivedCheckbox />}
                  </Stack>
                )}
              </Stack>

              {sidePanelProps && !selectedRowId && (
                <Stack sx={{ ml: "auto" }}>
                  <SidePanelTabs panelGroups={sidePanelProps.panelGroups} />
                </Stack>
              )}
            </>
          )}
        </WithInitialisedGridApiRef>
      </Stack>
    </Stack>
  );
};
type ToolbarTabProps = {
  selected: boolean;
  value: string;
  onChange: (value: string) => void;
  labelKey: TranslationKey;
};
const ToolbarTab = ({ selected, value, onChange, labelKey }: ToolbarTabProps) => {
  const { t } = useTranslation();
  const label = t(labelKey);
  return (
    <Button
      variant="text"
      size="small"
      onClick={() => onChange(value)}
      sx={{
        "display": "flex",
        "color": theme => theme.palette.text.primary,
        "borderRadius": "0",
        "borderBottom": selected ? theme => `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
        "px": 0,
        "minWidth": 0,
        /** Hack for preventing layout shift when selected tab changes, due to font weight */ "width": `max(${label.length * 8}px, 34px)`,
        "transition": "border-bottom-color 0.3s ease",
        "&.MuiButtonBase-root": { backgroundColor: "transparent" },
        "&:hover, &.Mui-focusVisible": {
          backgroundColor: "transparent",
          borderBottom: theme => `2px solid ${selected ? theme.palette.primary.main : theme.palette.grey["400"]}`,
        },
        ...(selected && { fontWeight: 600, pointerEvents: "none" }),
      }}
    >
      {label}
    </Button>
  );
};

const FilterCountBadge = () => {
  const gridApiRef = useInitialisedGridApiRef();
  const activeFilters = useGridSelector(gridApiRef, gridFilterActiveItemsSelector);
  const DIAMETER = 18;

  return (
    <Box
      sx={{
        visibility: activeFilters.length === 0 ? "hidden" : "visible",
        ml: 1,
        p: 0.5,
        display: "grid",
        placeContent: "center",
        minWidth: DIAMETER,
        height: DIAMETER,
        borderRadius: DIAMETER / 2,
        fontWeight: 700,
        fontSize: theme => theme.typography.pxToRem(12),
        bgcolor: theme => theme.palette.primary["500"],
        color: theme => theme.palette.primary.contrastText,
      }}
    >
      {activeFilters.length}
    </Box>
  );
};

export const ColumnFilterButton = () => {
  const { t } = useTranslation();
  const { setPanelTarget } = useGridContext();

  if (!setPanelTarget) throw new Error("setPanelTarget is required when using ColumnFilterButton");

  return (
    <PanelTriggerButton ref={setPanelTarget} panel="columns" startIcon={<Columns3Icon size={14} />}>
      {t("DynamicFormGrid.Toolbar.Columns")}
    </PanelTriggerButton>
  );
};

export const FilterButton = () => {
  const { t } = useTranslation();
  const { setPanelTarget } = useGridContext();

  if (!setPanelTarget) throw new Error("setPanelTarget is required when using FilterButton");

  return (
    <PanelTriggerButton ref={setPanelTarget} panel="filters" startIcon={<FunnelIcon size={14} />}>
      {t("DynamicFormGrid.Toolbar.Filter")}
      <FilterCountBadge />
    </PanelTriggerButton>
  );
};

const SHOW_ARCHIVED_QUERY_KEY = "show-archived";

export const useShowArchived = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const showArchived = searchParams.get(SHOW_ARCHIVED_QUERY_KEY) === "true";
  return {
    showArchived,
    toggleShowArchived: () => {
      if (showArchived) searchParams.delete(SHOW_ARCHIVED_QUERY_KEY);
      else searchParams.set(SHOW_ARCHIVED_QUERY_KEY, "true");
      setSearchParams(searchParams);
    },
  };
};

const ShowArchivedCheckbox = () => {
  const { t } = useTranslation();
  const { showArchived, toggleShowArchived } = useShowArchived();
  const checkboxId = useId();
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Checkbox size="small" id={checkboxId} checked={showArchived} onChange={toggleShowArchived} />
      <Box
        component="label"
        htmlFor={checkboxId}
        sx={{ fontSize: theme => theme.typography.pxToRem(12), whiteSpace: "nowrap" }}
      >
        <HACK_alignIconWithLabelBox>{t("DynamicFormGrid.Toolbar.ShowArchived")}</HACK_alignIconWithLabelBox>
      </Box>
    </Box>
  );
};

type PanelTriggerButtonProps = {
  panel: "columns" | "filters";
  children: ReactNode;
  startIcon: ReactNode;
  ref: Ref<HTMLButtonElement>;
};
const PanelTriggerButton = ({ panel, children, startIcon, ref }: PanelTriggerButtonProps) => {
  const gridApiRef = useInitialisedGridApiRef();
  const { openedPanelValue } = useGridSelector(gridApiRef, gridPreferencePanelStateSelector, undefined);
  /** When the panel is open, a click outside the panel will close it.
   *  However, if this toggle button is clicked it will open the panel again instead of closing it.
   *  To avoid this, we use a deferred value to get the opened panel value.
   */
  const deferedOpenedPanelValue = useDeferredValue(openedPanelValue);

  return (
    <ToggleButton
      ref={ref}
      value={panel}
      size="small"
      selected={deferedOpenedPanelValue === panel}
      onChange={() =>
        deferedOpenedPanelValue === panel
          ? gridApiRef.current.hidePreferences()
          : gridApiRef.current.showPreferences(GridPreferencePanelsValue[panel])
      }
    >
      {startIcon}
      {children}
    </ToggleButton>
  );
};

type ToolbarTabValue<T extends string> = { value: T };

export const useGetActiveToolbarTabValue = <TabValue extends string>() => {
  const [searchParams, setSearchParams] = useSearchParams();

  return useMemo(
    () => ({
      getActiveTabValue: (queryKey: string, tabs: ToolbarTabValue<TabValue>[]) => {
        const fallbackTab = tabs.at(0);
        if (!fallbackTab) throw new Error("At least one tab must be provided");

        const tab = tabs.find(tab => tab.value === searchParams.get(queryKey));
        return tab ? tab.value : fallbackTab.value;
      },
      setActiveTabValue: (queryKey: string, value: TabValue) => {
        searchParams.set(queryKey, value);
        setSearchParams(searchParams);
      },
    }),
    [searchParams, setSearchParams]
  );
};
